import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Dialog, DialogContent } from "../../../../components/UI/Dialog";
import { Tabs, TabsList, TabsTrigger } from "../../../../components/UI/Tabs";
import shopApi from "../../../../api/shopApi";
import useRecentSearches from "../../../../hooks/useRecentSearches";
import parseSearchSyntax from "../../../../utils/searchSyntax";
import SearchInput from "./SearchInput";
import SearchResults from "./SearchResults";
import FilterChips from "./FilterChips";
import { X } from "lucide-react";

const MIN_QUERY_LEN = 2;
const PAGE_SIZE = 50;
const URL_KEYS = { q: "q", tab: "stab" };

const EMPTY_FILTERS = {
  dateRange: null,
  unread: false,
  hasMedia: false,
  assignedToMe: false,
};

function friendlyErrorMessage(body, err) {
  const msg = body?.message || err?.message || "";
  if (/statement timeout|canceling statement/i.test(msg)) {
    return "Shop có quá nhiều tin nhắn. Thử filter 'Hôm nay' hoặc 'Có ảnh / file' để thu hẹp.";
  }
  if (msg) return msg;
  return "Không tìm được kết quả";
}

function messagesToPseudoContacts(messages) {
  const byContact = new Map();
  for (const m of messages) {
    const key =
      m.contactId || m.contactZaloId || m.uidFrom || m.id || m._id;
    if (!key) continue;
    const existing = byContact.get(key);
    if (!existing || (m.dateAction ?? "") > (existing.dateAction ?? "")) {
      byContact.set(key, m);
    }
  }
  return [...byContact.values()].map((m) => ({
    _id: m.contactId || m.contactZaloId || m.uidFrom || m.id || m._id,
    uid: m.contactZaloId || m.uidFrom || "",
    name: m.contactName || m.senderName || "(Không tên)",
    avatar: null,
    messages: [
      {
        content: m.content || "",
        dateAction: m.dateAction,
        msgId: m.msgId,
      },
    ],
    matchPriority: m.matchPriority,
  }));
}

function readUrlState() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      q: params.get(URL_KEYS.q) || "",
      tab: params.get(URL_KEYS.tab) === "messages" ? "messages" : "people",
    };
  } catch {
    return { q: "", tab: "people" };
  }
}

function syncUrlState(open, q, tab) {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (open && q && q.trim().length > 0) {
      params.set(URL_KEYS.q, q);
      params.set(URL_KEYS.tab, tab);
    } else {
      params.delete(URL_KEYS.q);
      params.delete(URL_KEYS.tab);
    }
    const search = params.toString();
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  } catch {
    /* ignore */
  }
}

const SearchContactModal = ({
  open,
  onOpenChange,
  shopId,
  staffId,
  onSelectConversation,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("people");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [results, setResults] = useState([]);
  const [subqueries, setSubqueries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);
  const { recent, push: pushRecent, remove: removeRecent, clear: clearRecent } =
    useRecentSearches();
  const successfulQueryRef = useRef("");
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const initializedFromUrlRef = useRef(false);

  // Hydrate from URL on first open
  useEffect(() => {
    if (!open || initializedFromUrlRef.current) return;
    initializedFromUrlRef.current = true;
    const { q, tab } = readUrlState();
    if (q) setSearchQuery(q);
    if (tab) setActiveTab(tab);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setResults([]);
      setSubqueries([]);
      setErrorMessage("");
      setFocusedIndex(0);
      setFilters(EMPTY_FILTERS);
      setHasMore(false);
      initializedFromUrlRef.current = false;
      syncUrlState(false, "", "people");
    }
  }, [open]);

  useEffect(() => {
    setFocusedIndex(0);
  }, [results, activeTab]);

  // URL sync (debounced via state change)
  useEffect(() => {
    if (!open) return;
    syncUrlState(open, searchQuery, activeTab);
  }, [open, searchQuery, activeTab]);

  // Parse query syntax (from:X, has:image, before:2026-04-01, etc.)
  const parsedSyntax = useMemo(
    () => parseSearchSyntax(searchQuery),
    [searchQuery],
  );
  const effectiveQuery = parsedSyntax.freeText || searchQuery;
  // Merge parsed syntax into filters (explicit chip settings override syntax)
  const effectiveFilters = useMemo(() => {
    const base = normalizeFiltersForTab(filters, activeTab, staffId);
    return {
      ...base,
      hasMedia: base.hasMedia || parsedSyntax.hasMedia,
      before: parsedSyntax.before || null,
      after: parsedSyntax.after || null,
      from: parsedSyntax.from || null,
      tag: parsedSyntax.tag || null,
      excludedText: parsedSyntax.excludedText,
      unread:
        base.unread ||
        parsedSyntax.isFlags.has("unread"),
      assignedToMe:
        base.assignedToMe ||
        parsedSyntax.isFlags.has("assigned") ||
        parsedSyntax.isFlags.has("mine"),
    };
  }, [filters, activeTab, staffId, parsedSyntax]);

  // Fetch first page when query/tab/filters change
  useEffect(() => {
    const q = (effectiveQuery || "").trim();
    if (q.length < MIN_QUERY_LEN) {
      setResults([]);
      setSubqueries([]);
      setIsLoading(false);
      setHasMore(false);
      setErrorMessage("");
      successfulQueryRef.current = "";
      return;
    }
    if (!shopId) {
      setResults([]);
      setIsLoading(false);
      setErrorMessage("Chưa chọn shop");
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const fn =
          activeTab === "people"
            ? shopApi.searchContacts
            : shopApi.searchMessages;
        const res = await fn(
          shopId,
          q,
          PAGE_SIZE,
          0,
          controller.signal,
          effectiveFilters,
        );
        if (controller.signal.aborted) return;
        if (res?.status !== 1) {
          setResults([]);
          setSubqueries([]);
          setHasMore(false);
          setErrorMessage(friendlyErrorMessage(res));
          return;
        }
        const rawData = res.data || [];
        const mapped =
          activeTab === "people"
            ? rawData
            : messagesToPseudoContacts(rawData);
        setResults(mapped);
        setSubqueries(res.subqueries || []);
        setHasMore(rawData.length >= PAGE_SIZE);
        if (rawData.length > 0 && successfulQueryRef.current !== q) {
          pushRecent(q);
          successfulQueryRef.current = q;
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        if (!controller.signal.aborted) {
          console.error("Search failed:", err);
          setErrorMessage(friendlyErrorMessage(err?.response?.data, err));
          setResults([]);
          setSubqueries([]);
          setHasMore(false);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    effectiveQuery,
    activeTab,
    shopId,
    pushRecent,
    // stringify filters so dependency array catches changes cleanly
    JSON.stringify(effectiveFilters),
    searchQuery,
  ]);

  const loadMore = useCallback(async () => {
    const q = (effectiveQuery || "").trim();
    if (!hasMore || isLoadingMore || isLoading) return;
    if (q.length < MIN_QUERY_LEN || !shopId) return;
    setIsLoadingMore(true);
    try {
      const fn =
        activeTab === "people"
          ? shopApi.searchContacts
          : shopApi.searchMessages;
      const res = await fn(
        shopId,
        q,
        PAGE_SIZE,
        results.length,
        undefined,
        effectiveFilters,
      );
      if (res?.status !== 1) {
        setHasMore(false);
        return;
      }
      const rawData = res.data || [];
      const mapped =
        activeTab === "people" ? rawData : messagesToPseudoContacts(rawData);
      setResults((prev) => {
        const seen = new Set(prev.map((r) => r._id));
        const merged = [...prev];
        for (const r of mapped) {
          if (!seen.has(r._id)) merged.push(r);
        }
        return merged;
      });
      setHasMore(rawData.length >= PAGE_SIZE);
    } catch (err) {
      console.error("Load more failed:", err);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    isLoading,
    effectiveQuery,
    shopId,
    activeTab,
    results.length,
    effectiveFilters,
  ]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: "120px" },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loadMore, results.length]);

  const handleSelectResult = useCallback(
    (result) => {
      if (!result) return;
      const isMessageResult = activeTab === "messages";
      const firstMessage = result.messages?.[0];
      const jumpTo =
        isMessageResult && firstMessage?.msgId
          ? {
              msgId: firstMessage.msgId,
              cliMsgId: firstMessage.cliMsgId,
              dateAction: firstMessage.dateAction,
            }
          : null;
      onSelectConversation(result, { isMessageResult, jumpTo });
      onOpenChange(false);
    },
    [activeTab, onSelectConversation, onOpenChange],
  );

  const handleClose = () => onOpenChange(false);

  const handleKeyDown = useCallback(
    (e) => {
      const hasResults = results.length > 0;
      if (e.key === "ArrowDown") {
        if (!hasResults) return;
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        if (!hasResults) return;
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        if (!hasResults) return;
        e.preventDefault();
        const picked = results[focusedIndex] ?? results[0];
        if (picked) handleSelectResult(picked);
      } else if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        setActiveTab((t) => (t === "people" ? "messages" : "people"));
      } else if (e.key === "Tab" && e.shiftKey) {
        e.preventDefault();
        setActiveTab((t) => (t === "messages" ? "people" : "messages"));
      }
    },
    [results, focusedIndex, handleSelectResult],
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const el = container.querySelector('[data-focused="true"]');
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [focusedIndex]);

  const showRecent =
    searchQuery.trim().length === 0 && recent.length > 0 && !errorMessage;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] p-0 gap-0 overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        <div className="p-4 pb-2 flex items-center">
          <div className="flex-1">
            <SearchInput
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              autoFocus={true}
            />
          </div>
          <button
            onClick={handleClose}
            className="ml-2 p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
            <span className="sr-only">Close</span>
          </button>
        </div>

        <Tabs
          defaultValue="people"
          value={activeTab}
          onValueChange={(value) => setActiveTab(value)}
          className="w-full"
        >
          <div className="border-b">
            <TabsList className="h-12 w-full rounded-none bg-transparent justify-start px-4 border-b-0">
              <TabsTrigger
                value="people"
                className="rounded-full px-4 py-1 data-[state=active]:bg-secondary data-[state=active]:shadow-none"
              >
                Danh bạ
              </TabsTrigger>
              <TabsTrigger
                value="messages"
                className="rounded-full px-4 py-1 data-[state=active]:bg-secondary data-[state=active]:shadow-none"
              >
                Tin nhắn
              </TabsTrigger>
            </TabsList>
          </div>

          <FilterChips
            activeTab={activeTab}
            filters={filters}
            onChange={setFilters}
            disabled={showRecent}
          />

          <div
            ref={scrollContainerRef}
            className="max-h-[400px] sm:max-w-[600px] overflow-y-auto"
          >
            {showRecent ? (
              <RecentSearchesPanel
                recent={recent}
                onPick={(q) => setSearchQuery(q)}
                onRemove={removeRecent}
                onClearAll={clearRecent}
              />
            ) : errorMessage && !isLoading ? (
              <div className="py-8 text-center text-red-500">
                <p>{errorMessage}</p>
              </div>
            ) : (
              <>
                <SearchResults
                  results={results}
                  isLoading={isLoading}
                  searchQuery={searchQuery}
                  activeTab={activeTab}
                  onSelectResult={handleSelectResult}
                  subqueries={subqueries}
                  focusedIndex={focusedIndex}
                />
                {hasMore && results.length > 0 && (
                  <div
                    ref={sentinelRef}
                    className="py-4 text-center text-xs text-gray-400"
                  >
                    {isLoadingMore ? "Đang tải thêm..." : "Cuộn xuống để xem thêm"}
                  </div>
                )}
                {!hasMore && results.length > 0 && !isLoading && (
                  <div className="py-4 text-center text-xs text-gray-300">
                    Hết kết quả
                  </div>
                )}
              </>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

function normalizeFiltersForTab(filters, tab, staffId) {
  const f = { ...filters };
  if (tab === "people") {
    f.hasMedia = false;
  } else {
    f.unread = false;
    f.assignedToMe = false;
  }
  if (f.assignedToMe && staffId) f.staffId = staffId;
  return f;
}

function RecentSearchesPanel({ recent, onPick, onRemove, onClearAll }) {
  return (
    <div className="py-3 px-2">
      <div className="flex items-center justify-between px-2 pb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Tìm kiếm gần đây
        </span>
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-gray-500 hover:text-red-500"
        >
          Xóa tất cả
        </button>
      </div>
      <ul className="flex flex-col">
        {recent.map((q) => (
          <li
            key={q}
            className="group flex items-center justify-between px-2 py-2 rounded-md hover:bg-secondary"
          >
            <button
              type="button"
              onClick={() => onPick(q)}
              className="flex-1 text-left text-sm text-gray-700 truncate"
            >
              {q}
            </button>
            <button
              type="button"
              onClick={() => onRemove(q)}
              aria-label={`Xóa ${q}`}
              className="ml-2 p-1 rounded-full text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-700 hover:bg-gray-100"
            >
              <X size={14} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SearchContactModal;
